import { useEffect, useState } from "react";

import { CLIENT_BUILD } from "../clientBuild";
import { readPrimaryEnvironmentTarget } from "../environments/primary";
import { useServerConfig } from "../rpc/serverState";
import {
  buildStaleClientKey,
  resolveVersionMismatch,
  wasStaleClientReloadRequested,
} from "../versionSkew";

export interface StaleClientBundleState {
  readonly staleKey: string;
}

/**
 * Reloading fetches the server's client build only for a window-origin target.
 * Prompting for another target can send the user round a reload loop.
 */
function isReloadableClientBundle(): boolean {
  if (import.meta.env.DEV) {
    return false;
  }
  return readPrimaryEnvironmentTarget()?.source === "window-origin";
}

export function useStaleClientBundle(): StaleClientBundleState | null {
  const serverConfig = useServerConfig();
  const clientBuild = serverConfig?.environment.clientBuild ?? null;
  const [preloadFailed, setPreloadFailed] = useState(false);
  const reloadBuild = clientBuild ?? CLIENT_BUILD;

  useEffect(() => {
    function onPreloadError(event: Event) {
      if (!isReloadableClientBundle() || reloadBuild === null) {
        return;
      }
      event.preventDefault();
      if (!wasStaleClientReloadRequested(reloadBuild)) {
        setPreloadFailed(true);
      }
    }
    window.addEventListener("vite:preloadError", onPreloadError);
    return () => window.removeEventListener("vite:preloadError", onPreloadError);
  }, [reloadBuild]);

  if (!isReloadableClientBundle()) {
    return null;
  }
  const mismatch = resolveVersionMismatch(clientBuild);
  const staleKey = mismatch ? buildStaleClientKey(mismatch) : preloadFailed ? reloadBuild : null;
  return staleKey !== null && !wasStaleClientReloadRequested(staleKey) ? { staleKey } : null;
}
